/**
 * Cashflow Snapshot Page
 * Firm-wide cash flow overview with per-client drill-down.
 * Data: GET /compliance/cashflow (firm summary + per-client list)
 *       GET /compliance/cashflow/{clientId} (single client detail)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Wallet, TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight, ArrowLeft, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

const fmtCurrency = (v: number) =>
  `${v < 0 ? '-' : ''}$${Math.abs(Math.round(v)).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

interface MonthlyData {
  month: string;
  label: string;
  revenue: number;
  expenses: number;
  netCashFlow: number;
  isProjected: boolean;
  confidenceLow?: number;
  confidenceHigh?: number;
}

interface ClientSummary {
  avgMonthlyRevenue: number;
  avgMonthlyExpenses: number;
  avgNetCashFlow: number;
  revenueGrowthRate: number;
  projectedCashIn6Months: number;
  trend: 'growing' | 'stable' | 'declining';
}

interface CashFlowResult {
  clientId: string;
  clientName: string;
  currency: string;
  historical: MonthlyData[];
  projected: MonthlyData[];
  summary: ClientSummary;
  recommendations: string[];
}

interface FirmSummary {
  totalClients: number;
  clientsWithData: number;
  growing: number;
  stable: number;
  declining: number;
  totalProjected6Months: number;
}

interface FirmResponse {
  summary: FirmSummary;
  clients: CashFlowResult[];
}

const TREND_STYLE: Record<string, string> = {
  growing: 'text-green-600 dark:text-green-400 bg-green-500/10',
  stable: 'text-blue-600 dark:text-blue-400 bg-blue-500/10',
  declining: 'text-red-600 dark:text-red-400 bg-red-500/10',
};

function TrendIcon({ trend, className }: { trend: string; className?: string }) {
  if (trend === 'growing') return <TrendingUp className={cn('text-green-600 dark:text-green-400', className)} />;
  if (trend === 'declining') return <TrendingDown className={cn('text-red-600 dark:text-red-400', className)} />;
  return <Minus className={cn('text-muted-foreground', className)} />;
}

/** Horizontal CSS bar list of monthly net cash flow buckets. */
function BucketBars({ buckets }: { buckets: MonthlyData[] }) {
  if (buckets.length === 0) return null;
  const max = Math.max(...buckets.map((b) => Math.abs(b.netCashFlow)), 1);
  return (
    <div className="space-y-2">
      {buckets.map((b) => {
        const pct = (Math.abs(b.netCashFlow) / max) * 100;
        const positive = b.netCashFlow >= 0;
        return (
          <div key={b.month} className="flex items-center gap-3">
            <span className={cn('text-xs w-16 shrink-0', b.isProjected ? 'text-muted-foreground italic' : 'text-foreground')}>
              {b.label.replace(/ \d{4}$/, '')}
            </span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', positive ? 'bg-[var(--v4-accent)]' : 'bg-red-500', b.isProjected && 'opacity-50')}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </div>
            <span className={cn('text-xs w-16 text-right shrink-0', positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              {fmtCurrency(b.netCashFlow)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Per-client detail view (drill-down). */
function ClientDetail({ clientId, onBack }: { clientId: string; onBack: () => void }) {
  const { data, isLoading, isError } = useQuery<CashFlowResult>({
    queryKey: ['cashflow-client', clientId],
    queryFn: async () => {
      const r = await apiClient.get(`/compliance/cashflow/${clientId}`);
      return r.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-start gap-3 p-4 bg-red-500/5 border border-red-500/10 rounded-xl">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">Could not load cash flow for this client.</p>
        </div>
      </div>
    );
  }

  const { summary, historical, projected } = data;
  const allBuckets = [...historical, ...projected];
  const hasData = historical.length >= 3;

  return (
    <div className="px-5 py-4 max-w-lg mx-auto space-y-5 pb-24">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold truncate">{data.clientName}</h1>
          <p className="text-sm text-muted-foreground">Cash flow snapshot ({data.currency})</p>
        </div>
        <span className={cn('text-xs px-2 py-1 rounded capitalize flex items-center gap-1 shrink-0', TREND_STYLE[summary.trend])}>
          <TrendIcon trend={summary.trend} className="h-3.5 w-3.5" />
          {summary.trend}
        </span>
      </div>

      {!hasData ? (
        <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            {data.recommendations[0] || 'Not enough accounting data to build a cash flow view for this client.'}
          </p>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-xl p-4">
              <ArrowUpRight className="h-5 w-5 text-green-600 dark:text-green-400 mb-2" />
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{fmtCurrency(summary.avgMonthlyRevenue)}</p>
              <p className="text-xs text-muted-foreground">Avg Monthly Inflow</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <ArrowDownRight className="h-5 w-5 text-red-600 dark:text-red-400 mb-2" />
              <p className="text-lg font-bold text-red-600 dark:text-red-400">{fmtCurrency(summary.avgMonthlyExpenses)}</p>
              <p className="text-xs text-muted-foreground">Avg Monthly Outflow</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <Wallet className="h-5 w-5 text-[var(--v4-accent)] mb-2" />
              <p className={cn('text-lg font-bold', summary.avgNetCashFlow >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {fmtCurrency(summary.avgNetCashFlow)}
              </p>
              <p className="text-xs text-muted-foreground">Avg Net / Month</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <TrendIcon trend={summary.trend} className="h-5 w-5 mb-2" />
              <p className={cn('text-lg font-bold', summary.projectedCashIn6Months >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {fmtCurrency(summary.projectedCashIn6Months)}
              </p>
              <p className="text-xs text-muted-foreground">Projected Net (6mo)</p>
            </div>
          </div>

          {/* Bucket bars */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Net Cash Flow by Month</h3>
              <span className="text-xs text-muted-foreground">faded = projected</span>
            </div>
            <BucketBars buckets={allBuckets} />
          </div>

          {/* Recommendations */}
          {data.recommendations.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Insights</h3>
              {data.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl">
                  <TrendingUp className="h-4 w-4 text-[var(--v4-accent)] shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">{rec}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CashflowPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<FirmResponse>({
    queryKey: ['cashflow-firm'],
    queryFn: async () => {
      const r = await apiClient.get('/compliance/cashflow');
      return r.data.data;
    },
  });

  if (selected) {
    return <ClientDetail clientId={selected} onBack={() => setSelected(null)} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto space-y-4">
        <div>
          <h2 className="text-xl font-medium">Cash Flow</h2>
          <p className="text-sm text-muted-foreground">Firm-wide snapshot</p>
        </div>
        <div className="flex items-start gap-3 p-4 bg-red-500/5 border border-red-500/10 rounded-xl">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">Could not load cash flow data. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, clients } = data;
  const withData = clients.filter((c) => c.historical.length >= 3);
  const net6 = summary.totalProjected6Months;

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-medium">Cash Flow</h2>
        <p className="text-sm text-muted-foreground">Firm-wide snapshot across {summary.clientsWithData} of {summary.totalClients} clients</p>
      </div>

      {summary.clientsWithData === 0 ? (
        <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            No cash flow data yet. Connect clients to Xero or QuickBooks to build forecasts.
          </p>
        </div>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-xl p-4 col-span-2">
              <Wallet className="h-5 w-5 text-[var(--v4-accent)] mb-2" />
              <p className={cn('text-2xl font-bold', net6 >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {fmtCurrency(net6)}
              </p>
              <p className="text-sm text-muted-foreground">Projected Net Cash Position (next 6 months)</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400 mb-2" />
              <p className="text-2xl font-bold">{summary.growing}</p>
              <p className="text-sm text-muted-foreground">Growing</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400 mb-2" />
              <p className="text-2xl font-bold">{summary.declining}</p>
              <p className="text-sm text-muted-foreground">Declining</p>
            </div>
          </div>

          {/* Client picker / breakdown */}
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Clients</h3>
            {withData.length === 0 && (
              <p className="text-sm text-muted-foreground">No clients have enough accounting history yet.</p>
            )}
            {withData.map((c) => (
              <button
                key={c.clientId}
                onClick={() => setSelected(c.clientId)}
                className="w-full flex items-center gap-3 py-3 border-b border-border last:border-0 text-left transition hover:opacity-80"
              >
                <TrendIcon trend={c.summary.trend} className="h-4 w-4 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{c.clientName}</p>
                  <p className="text-xs text-muted-foreground">Avg net {fmtCurrency(c.summary.avgNetCashFlow)}/mo</p>
                </div>
                <span className={cn('text-sm font-medium shrink-0', c.summary.projectedCashIn6Months >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                  {fmtCurrency(c.summary.projectedCashIn6Months)}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
